// src/templates/project.js

import React from 'react';
import { graphql, Link } from 'gatsby';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import styled from 'styled-components';
import { Layout } from '@components';
import { Icon } from '@components/icons';

/* Same construction as the blog template: no width or padding of its own, so
   the global main rule supplies the page gutters that keep the column clear of
   the fixed side rails, and a 700px column inside centres it. */
const StyledProjectContainer = styled.main`
  .inner {
    max-width: 700px;
    margin: 0 auto;
  }
`;

const StyledProjectHeader = styled.header`
  margin-bottom: var(--space-7);

  .project-title {
    margin: 0;
    font-size: clamp(28px, 4vw, var(--fz-heading));
    line-height: var(--lh-title);
    letter-spacing: -0.02em;
    color: var(--text);
  }

  /* One row, separated by space rather than punctuation — the same meta line
     the blog template uses, so a project page and a blog post share a rhythm. */
  .meta {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: var(--space-2);
    margin-top: var(--space-2);
    color: var(--text-muted);
    font-size: var(--fz-md);

    > * + *::before {
      content: '·' / '';
      margin-right: var(--space-2);
      color: var(--text-disabled);
    }

    .company {
      color: var(--text-secondary);
    }

    .tags {
      display: flex;
      flex-wrap: wrap;
      gap: var(--space-2);

      &::before {
        margin-right: 0;
      }
    }

    .tag:hover,
    .tag:focus-visible {
      color: var(--accent);
    }
  }

  /* The metadata the cards deliberately leave off. A label in front of each
     row, so neither reads as loose decoration. */
  .detail-row {
    ${({ theme }) => theme.mixins.resetList};
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--space-3);
    margin-top: var(--space-2);

    .detail-label {
      color: var(--text-secondary);
      font-size: var(--fz-md);
      margin-right: var(--space-1);
    }

    li {
      color: var(--text-muted);
      font-size: var(--fz-md);
    }

    a {
      display: flex;
      align-items: center;
      gap: var(--space-2);
      color: var(--text-muted);
      font-size: var(--fz-md);
      transition: var(--transition);

      &:hover,
      &:focus-visible {
        color: var(--accent);
      }

      svg {
        width: 16px;
        height: 16px;
      }
    }
  }
`;

const StyledProjectContent = styled.div`
  margin-bottom: var(--space-4);

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin: 2em 0 0em;
  }

  p,
  ul,
  ol,
  blockquote {
    max-width: var(--measure);
  }

  p {
    margin: 1em 0;
    line-height: var(--lh-body);
    font-size: var(--fz-lg);
    color: var(--text-secondary);
  }

  a {
    ${({ theme }) => theme.mixins.inlineLink};
  }

  code {
    background-color: var(--border);
    color: var(--text-emphasis);
    border-radius: var(--border-radius);
    font-size: var(--fz-sm);
    padding: 0.2em 0.4em;
  }

  pre code {
    background-color: transparent;
    padding: 0;
  }
`;

const ProjectTemplate = ({ data, location }) => {
  const { frontmatter, html } = data.markdownRemark;
  // `description` is queried but not rendered here — the cards carry it, and
  // the page opens straight into the write-up.
  const { title, date, tags, tech, company, github, external, instagram, ios, android } =
    frontmatter;

  const formattedDate = new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
  });

  // Fixed order so the links sit in the same place from project to project;
  // one without a given link simply drops it.
  const links = [
    { name: 'GitHub', label: 'Source', url: github },
    { name: 'Instagram', label: 'Instagram', url: instagram },
    { name: 'External', label: 'Live', url: external },
    { name: 'AppStore', label: 'App Store', url: ios },
    { name: 'PlayStore', label: 'Google Play', url: android },
  ].filter(link => link.url);

  return (
    <Layout location={location}>
      <StyledProjectContainer>
        <div className="inner">
          <StyledProjectHeader>
            <Helmet title={title} />
            <span className="breadcrumb">
              <Link to="/projects">
                <span className="arrow">&larr; Back</span>
              </Link>
            </span>
            <h1 className="project-title">{title}</h1>

            <div className="meta">
              <time>{formattedDate}</time>
              {company ? <span className="company">{company}</span> : null}
              {tags?.length > 0 && (
                <span className="tags">
                  {tags.map(tag => (
                    <Link
                      key={tag}
                      to={`/projects?tag=${encodeURIComponent(tag)}`}
                      className="tag">
                      #{tag}
                    </Link>
                  ))}
                </span>
              )}
            </div>

            {tech?.length > 0 && (
              <ul className="detail-row">
                <li className="detail-label">Built with</li>
                {tech.map(item => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            )}

            {links.length > 0 && (
              <ul className="detail-row">
                <li className="detail-label">Find it on</li>
                {links.map(({ name, label, url }) => (
                  <li key={name}>
                    <a href={url} target="_blank" rel="noopener noreferrer">
                      <Icon name={name} />
                      {label}
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </StyledProjectHeader>

          {html?.trim() ? (
            <StyledProjectContent dangerouslySetInnerHTML={{ __html: html }} />
          ) : null}
        </div>
      </StyledProjectContainer>
    </Layout>
  );
};

ProjectTemplate.propTypes = {
  data: PropTypes.shape({
    markdownRemark: PropTypes.shape({
      html: PropTypes.string,
      frontmatter: PropTypes.object.isRequired,
    }),
  }),
  location: PropTypes.object,
};

export default ProjectTemplate;

export const pageQuery = graphql`
  query ($path: String!) {
    markdownRemark(frontmatter: { slug: { eq: $path } }) {
      html
      frontmatter {
        title
        slug
        description
        date
        tags
        tech
        company
        github
        external
        instagram
        ios
        android
      }
    }
  }
`;
